<script lang="ts" setup>
import {computed, ref} from "vue";
import {useRoute} from "vue-router";
import type {FacadeType} from "~/graphql/house-land-packages";
import {useHouseAndLandPackageByUri} from "~/composables/use-house-land-package";
import {useStrippedString} from "~/composables/use-strings";
import TabPane from "~/components/HouseAndLandPackages/TabPane.vue";
import EstateInformation from "~/components/HouseAndLandPackages/EstateInformation.vue";

const route = useRoute();
const page = await useHouseAndLandPackageByUri(route.path.toString());
if (!page) {
  throw createError({
    statusMessage: "House and Land Package not found",
    statusCode: 404,
  });
}

const selected = ref(0);
const pkg = page?.houseAndLandPackageFields;
const variation = computed(() => pkg?.variation[selected?.value]);
const strippedDescription = computed(() => useStrippedString(variation.value.description));
const facades = computed(() => pkg
        ?.variation[selected?.value]
        ?.facade
        ?.map((item: FacadeType) => ({
          image: item.image,
          caption: `${item.name} | ${item.scheme}`
        })) ?? []
);

const contactFormProps = {
  formPosition: ['left'],
  backgroundColor: '#8B8C6A',
  showSocialMediaIcons: true,
  textColors: {
    titleColor: '#E3DCCC',
    bodyColor: '#E3DCCC',
    iconTitleColor: '#E3DCCC',
    iconColor: '#8b8c6a',
    iconBackgroundColor: '#E3DCCC'
  }
}

definePageMeta({layout: "home-design"});
useSeoData(page?.seo || {});
</script>
<template>
  <section class="bg-bottom-right house-land-wrapper">
    <div class="section-spacer-lg"></div>
    <Slider :imageSlides="facades" :slideId="`slide-group-${selected}`"/>
    <div class="section-spacer"></div>
    <div class="container-xl hl-div-wrapper">
      <div class="hero-header w-100 d-flex flex-column flex-md-row align-items-stretch justify-content-between">
        <div class="col-12 col-md d-flex flex-column align-items-start justify-content-center">
          <p class="h2 text-white">{{ page.title }}</p>
          <p class="mb-0 h3 text-secondary">${{ new Intl.NumberFormat().format(variation?.price) }}</p>
        </div>
        <div class="col-12 col-md d-flex flex-row flex-nowrap justify-content-end align-items-start mt-3">
          <div class="text-nowrap text-secondary">
            <span>{{ variation.bed }} Bed</span>
            <span class="mx-3">•</span>
            <span>{{ variation.bath }} Bath</span>
            <span class="mx-3">•</span>
            <span>{{ variation.bath }} Car</span>
            <span class="mx-3">•</span>
            <span>{{ variation.houseSize }} m²</span>
          </div>
        </div>
      </div>
      <div class="w-100 variation-description" v-html="strippedDescription"></div>
      <div class="w-100 tabbed-variation">
        <ul class="nav nav-tabs border-0" role="tablist">
          <li v-for="(v, k) in pkg?.variation"
              class="nav-item me-2" role="presentation">
            <button
                @click.prevent="() => selected = k"
                :class="['fs-5 px-4 nav-link border-0 shadow-none', {active: selected === k}]"
                data-bs-toggle="tab" type="button" role="tab">{{ v.name }}
            </button>
          </li>
        </ul>
        <div class="tab-content bg-secondary rounded">
          <div class="tab-pane show active" role="tabpanel" tabindex="0">
            <TabPane
                popup=""
                :image="variation?.plan?.image"
                :length="variation?.houseLength || 0"
                :width="variation?.houseWidth || 0"
                :size="variation?.houseSize || 0"
                :download="variation?.plan?.download"
                :features="variation?.features?.map(item => item.feature) || []"
                :designOptions="variation?.designOptions?.map(item => item.option) || []"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="section-spacer"></div>
    <Accordion
        :title="pkg?.details.title"
        :description="pkg?.details?.description"
        :items="pkg?.details?.items?.map((item) => ({title: item.heading, description: item.contents}))"
    />
    <div class="section-spacer"></div>
    <ImageHeroSlider :isReversed="false" sliderId="estate-info" :slides="[
      {
        description: pkg?.estateInformation?.description,
        title: pkg?.estateInformation?.title,
        link: pkg?.estateInformation?.link,
        image: pkg?.estateInformation?.image
      }
    ]"/>
    <div class="section-spacer"></div>
    <div class="section-spacer"></div>
    <div id="contact-form-section">
      <ContactForm v-bind="contactFormProps" :bgOnMobile="true"/>
    </div>
    <div class="section-spacer"></div>
    <Newsletter :isEnabled="true" :overFlowSection="true"/>
  </section>
</template>

<style lang="css" scoped>

.hero-header {
  background-color: var(--bs-color-light);
  border-radius: 1.5rem;
  margin-bottom: 30px;
  padding: 25px 30px;
}

.btn-variation {
  color: var(--var-dark-color);

  &.active {
    color: var(--bs-secondary);
  }
}

.variation-description {
  min-height: 150px;
  margin-bottom: 4rem;
}

.tabbed-variation {
  .rounded {
    border-radius: 1.5rem !important;
  }

  .nav-item {
    .nav-link {
      color: var(--var-dark-color);
      border-radius: 1rem !important;
      border-bottom-left-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      background-color: var(--tab-btn-background-color);

      &.active {
        background-color: var(--bs-secondary);
      }
    }
  }

  .tab-content {
    border-top-left-radius: 0 !important;
    padding: 30px;

    .tab-pane:focus-visible {
      outline: none !important;
      box-shadow: none !important;
    }

    .btn-show-floor-plan {
      line-height: 1rem !important;
    }
  }
}
</style>
